import React from 'react'
import { DeleteBulkAction, DeleteBulkWithFiltersAction } from './delete-action'
import { RowSelection } from '../../../legacy-design-system/grid/grid'
import { ActionList } from '../../../legacy-design-system/action'
import { PureQueryOptions } from 'apollo-client'
import { Role } from '@contrast-security-inc/base-types'
import { Server, ServerAggregationCategory } from '../shared'
import {
  ModifyServerTagsAction,
  ModifyServerTagsByFilterAction
} from './tag-action'
import {
  ModifyBulkServerSettingsAction,
  ModifyServerSettingsByFilterAction,
  ServersGridFilters
} from './server-settings-action'
import { AgentFeature } from '../../agent-features'
import { useQueryClient } from 'react-query'
import { refreshAndInvalidateVrLicenseQueries } from './shared'

interface ActionProps {
  organizationRole: Role
  organizationID: string
  isFreemium: boolean
  refetchQueries: PureQueryOptions[]
  refresh: () => Promise<any>

  /**
   * True if the current user is a Protect user
   */
  protect: boolean

  /**
   * The current organization's available agent features
   */
  agentFeatures: AgentFeature[]
}

export function BulkActionList({
  invertedSelection,
  selectedItems,
  deselectedItems,
  filters: { filterText, quickFilter, aggregations },
  organizationRole,
  organizationID,
  isFreemium,
  refetchQueries,
  refresh,
  agentFeatures,
  protect
}: RowSelection<Server, ServerAggregationCategory> & ActionProps) {
  const queryClient = useQueryClient()

  function licenseRelatedActionRefresh() {
    return refreshAndInvalidateVrLicenseQueries({ queryClient, refresh })
  }

  if (invertedSelection) {
    const filters: ServersGridFilters = Object.assign(
      {},
      {
        filterText,
        quickFilter,
        excludedServers: deselectedItems.map(({ id }) => id)
      },
      aggregations
    )

    return selectedItems.length > 0 ? (
      <ActionList large>
        <ModifyServerSettingsByFilterAction
          filters={filters}
          organizationID={organizationID}
          organizationRole={organizationRole}
          isFreemium={isFreemium}
          protect={protect}
          agentFeatures={agentFeatures}
          refetchQueries={refetchQueries}
          refresh={refresh}
          servers={selectedItems}
        />
        <ModifyServerTagsByFilterAction
          filters={filters}
          excludedServers={deselectedItems}
          organizationID={organizationID}
          organizationRole={organizationRole}
          refetchQueries={refetchQueries}
          refresh={refresh}
          servers={selectedItems}
          bulk
        />
        <DeleteBulkWithFiltersAction
          filters={filters}
          organizationID={organizationID}
          organizationRole={organizationRole}
          isFreemium={isFreemium}
          protect={protect}
          agentFeatures={agentFeatures}
          refetchQueries={refetchQueries}
          refresh={licenseRelatedActionRefresh}
          servers={selectedItems}
        />
      </ActionList>
    ) : null
  }

  const props = {
    servers: selectedItems,
    isFreemium,
    organizationRole,
    organizationID,
    refetchQueries,
    refresh,
    agentFeatures,
    protect
  }

  return selectedItems.length > 0 ? (
    <ActionList large>
      <ModifyBulkServerSettingsAction {...props} />
      <ModifyServerTagsAction bulk {...props} />
      <DeleteBulkAction {...props} refresh={licenseRelatedActionRefresh} />
    </ActionList>
  ) : null
}
